import { DestroyRef, EventEmitter, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { COMMONS_TRANSLATIONS_PROVIDER } from '@jump-tech-frontend/app-config';
import { BehaviorSubject, debounceTime, filter } from 'rxjs';
import { AddressProvider } from '../../domain/address-provider';
import { IAddressV2 } from '@jump-tech-frontend/domain';
import { ISearchProvider } from '../../domain/search-provider';
import { SearchProviderFactory } from '../../providers/search-provider.factory';
import { AddressSearchResultType, AddressSearchViewModel } from './address-search.vm';

@Injectable()
export class AddressSearchPresenter {
  private readonly translationsProvider = inject(COMMONS_TRANSLATIONS_PROVIDER);
  private readonly searchProviderFactory = inject(SearchProviderFactory);
  private destroyRef = inject(DestroyRef);
  private noResult$ = new BehaviorSubject<boolean>(false);
  private searchProvider: ISearchProvider;
  private searchResults$ = new BehaviorSubject<
    { id: string; description: string; type?: AddressSearchResultType }[] | null
  >(null);
  public selectedResult: EventEmitter<IAddressV2>;
  public selectedIndex = -1;

  load(
    vm$: BehaviorSubject<AddressSearchViewModel | null>,
    countryCodes: string[],
    selectedResult: EventEmitter<IAddressV2>,
    label?: string,
    provider?: AddressProvider,
    disabled?: boolean,
    required?: boolean,
    address?: IAddressV2 | null,
    errorMessage?: string,
    placeholder?: string
  ) {
    this.searchProvider = this.searchProviderFactory.getProvider(countryCodes, provider);
    const searchTermControl = this.makeSearchFormControl(disabled, required);
    this.selectedResult = selectedResult;
    vm$.next({
      provider: this.searchProvider.name,
      countryCodes,
      label,
      placeholder: placeholder ? placeholder : '',
      noResult$: this.noResult$,
      noResultsFound: this.translationsProvider.getTranslation('noResultsFound') ?? '',
      searchResults$: this.searchResults$,
      searchTermControl,
      showError: searchTermControl?.invalid && searchTermControl?.touched && !address && !!errorMessage
    });
  }

  private makeSearchFormControl(disabled?: boolean, required?: boolean) {
    const validator = required ? [Validators.required] : null;
    const searchTermControl = new FormControl<string>({ value: '', disabled: !!disabled }, validator);

    searchTermControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(200),
        filter(searchTerm => (searchTerm ?? '').length > 3)
      )
      .subscribe(async searchTerm => {
        if (searchTerm) {
          await this.search(searchTerm);
        }
      });
    return searchTermControl;
  }

  async search(searchTermEvent: string, container?: string | undefined) {
    this.noResult$.next(false);
    this.selectedIndex = -1;
    const searchResults = await this.searchProvider.search(searchTermEvent, container);
    if (!searchResults.length) {
      this.noResult$.next(true);
    }
    this.searchResults$.next(searchResults);
  }

  async retrieve(id: string, searchTerm: string) {
    this.searchResults$.next([]);
    this.selectedResult.next(await this.searchProvider.getResult(id, searchTerm));
  }

  async searchOrRetrieve(id: string, searchTerm: string, type: AddressSearchResultType) {
    if (type === 'Address' || type === 'ADD') {
      await this.retrieve(id, searchTerm);
    } else {
      await this.search(searchTerm, id);
    }
  }

  navigateDown(): number {
    const length = this.searchResults$.value?.length ?? 0;
    if (!length) return -1;
    this.selectedIndex = this.selectedIndex < length - 1 ? this.selectedIndex + 1 : 0;
    return this.selectedIndex;
  }

  navigateUp(): number {
    const length = this.searchResults$.value?.length ?? 0;
    if (!length) return -1;
    this.selectedIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : length - 1;
    return this.selectedIndex;
  }

  getSelectedResult(): { id: string; type: AddressSearchResultType } | null {
    if (this.selectedIndex < 0) return null;
    const result = this.searchResults$.value?.[this.selectedIndex];
    return result ? { id: result.id, type: result.type ?? 'Address' } : null;
  }
}
